import { useRef, useState } from "react";
import { bool, arrayOf, func, string, oneOf } from "prop-types";
import { DEFAULT_LABEL_COLOR, DEFAULT_LABEL_COLORS, DEFAULT_LABEL_FONT_COLOR, DEFAULT_LABEL_FONT_COLORS } from "@utils";
import { InfoLabel, PopMenu, Button } from "@components";
import { capitalize, Divider, Grid } from "@material-ui/core";
import { ColorSquare } from "./ColorSquare";
import { NewColorButton } from "./NewColorButton";
import { makeStyles } from "@material-ui/styles"
import classnames from "classnames";

export const LabelColorPicker = ({
  backgroundColor,
  fontColor,
  additionalColors=[],
  additionalFontColors=[],
  onConfirm,
  onColorChange,
  onFontColorChange,
  onAdditionalColorChange,
  onAdditionalColorConfirm,
  onAdditionalFontColorChange,
  onAdditionalFontColorConfirm,
  className,
  disabled,
  tooltipText,
  hoverIcon,
  size="large",
  buttonType="default",
}) => {
  const classes = useStyle();
  
  const [ pickerVisible, showPicker ] = useState(false);
  
  const ref = useRef(null);
  
  const handleConfirmChange = async () => {
    showPicker(false);
    await onConfirm?.();
  }
  
  return (
    <>
      <Button
        ref={ref}
        color="secondary"
        variant={buttonType === "dot" ? "contained" : "outlined"}
        className={classnames(
          buttonType === "dot" ? "circle w-12 h-12 ml-2" : classes.block,
          buttonType === "dot" && hoverIcon && !pickerVisible && "hoverIcon",
          buttonType === "default" && classes[`block${capitalize(size)}`],
          className,
        )}
        style={buttonType === "dot" ? { backgroundColor: backgroundColor || DEFAULT_LABEL_COLOR, minWidth: 0, padding: 0 } : undefined}
        onClick={(event) => {
          if (buttonType === "dot") event.stopPropagation();
          showPicker(true);
        }}
        disabled={disabled}
        tooltipText={tooltipText}
      >
        {buttonType === "default" && <Grid
          item container
          justifyContent="center"
          alignItems="center"
          className={classnames(
            classes.sample,
            classes[`sample${capitalize(size)}`],
            )}
          style={{
            color: fontColor || DEFAULT_LABEL_FONT_COLOR,
            backgroundColor: backgroundColor || DEFAULT_LABEL_COLOR,
        }}
        >
          {"Ab"}
        </Grid>}
      </Button>
      <PopMenu
        show={pickerVisible}
        onClickAway={handleConfirmChange}
        anchor={ref.current}
        style={{ width: 424 }}
        placement={buttonType === "dot" ? "right" : "bottom"}
      >
        <Grid
          item container
          className="px-5 pt-6 pb-5"
        >
          <InfoLabel label="views.settings.projects.labels.select_color" spacing={0} />
          <Grid item container>
            {
              [
                ...DEFAULT_LABEL_COLORS,
                ...additionalColors
              ].map((c, i) => (
                <ColorSquare key={c+i} hex={c} onSelect={onColorChange} selected={c === backgroundColor} />
              ))
            }
            <NewColorButton
              selectedColor={backgroundColor}
              onChange={onAdditionalColorChange}
              onComplete={onAdditionalColorConfirm}
            />
          </Grid>
          <Divider className="w-full mt-3" />
          <InfoLabel label="views.settings.projects.labels.select_font_color" />
          <Grid item container>
            {
              [
                ...DEFAULT_LABEL_FONT_COLORS,
                ...additionalFontColors
              ].map((c, i) => (
                <ColorSquare key={c+i} hex={c} onSelect={onFontColorChange} selected={c === fontColor} />
              ))
            }
            <NewColorButton
              selectedColor={fontColor}
              onChange={onAdditionalFontColorChange}
              onComplete={onAdditionalFontColorConfirm}
            />
          </Grid>
        </Grid>
      </PopMenu>
    </>
  )
}

LabelColorPicker.propTypes = {
  backgroundColor: string,
  fontColor: string,
  className: string,
  additionalColors: arrayOf(string),
  additionalFontColors: arrayOf(string),
  onConfirm: func,
  onColorChange: func.isRequired,
  onFontColorChange: func.isRequired,
  onAdditionalColorChange: func.isRequired,
  onAdditionalColorConfirm: func,
  onAdditionalFontColorChange: func.isRequired,
  onAdditionalFontColorConfirm: func,
  disabled: bool,
  size: oneOf([ "small", "medium", "large" ]),
  buttonType: oneOf(["default", "dot"]),
}


const useStyle = makeStyles((theme) => ({
  block: {
    borderRadius: 5,
    padding: 0,
    border: `1px solid ${theme.props.border}`,
  },
  blockLarge: {
    height: 48,
    width: 48,
    minWidth: 48,
  },
  blockMedium: {
    height: 40,
    width: 40,
    minWidth: 40,
  },
  blockSmall: {
    height: 32,
    width: 32,
    minWidth: 32,
  },
  sample: {
    height: 24,
    width: 24,
    borderRadius: 5,
    fontSize: 12
  },
  sampleSmall: {
    height: 20,
    width: 20,
  }
}));
